/**
 * About Page Component - IT Engine Sparks
 * Feature-sliced design: Clean, modular architecture
 */
import { Box, Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { HeroSection } from "./sections";

const About = () => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t("about.title")}</title>
        <meta name="description" content={t("about.description")} />
        <meta property="og:title" content={t("about.ogTitle")} />
        <meta property="og:description" content={t("about.ogDescription")} />
        <meta property="og:image" content="https://itenginesparks.com/og-banner.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="IT Engine Sparks — Web & Software Development" />
        <meta property="og:url" content="https://itenginesparks.com/about" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://itenginesparks.com/about" />
        <meta name="twitter:title" content={t("about.ogTitle")} />
        <meta name="twitter:description" content={t("about.description")} />
        <meta name="twitter:image" content="https://itenginesparks.com/og-banner.png" />
      </Helmet>

      {/* Hero Banner */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "52vh", md: "60vh" },
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          backgroundImage: "url('/images/about.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          backgroundRepeat: "no-repeat",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            // Left half very dark for text, right half semi-transparent to show Carlos
            background: "linear-gradient(100deg, rgba(8,18,38,0.97) 0%, rgba(8,18,38,0.85) 40%, rgba(8,18,38,0.45) 70%, rgba(8,18,38,0.20) 100%)",
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 2, py: { xs: 6, md: 8 } }}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            style={{ maxWidth: "50%" }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                color: "#fff",
                letterSpacing: "-0.02em",
                textShadow: "0 2px 20px rgba(0,0,0,0.5)",
                mb: 1,
                textAlign: "left",
              }}
            >
              {t("about.heading")}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontWeight: 400,
                textAlign: "left",
                textShadow: "0 1px 8px rgba(0,0,0,0.6)",
              }}
            >
              {t("about.role")}
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: "inline-block",
                bgcolor: "rgba(255,109,0,0.90)",
                color: "#fff",
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.85rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              {t("about.experience")}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Bio Content */}
      <Container maxWidth="md" sx={{ mt: 5, mb: 8 }}>
        <HeroSection />
      </Container>
    </Box>
  );
};

export default About;
